/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';

export interface PromptField {
  key: string;
  /** Translation key or text. */
  label: string;
  value?: string;
  required?: boolean;
  /** Translation key or text shown under the field. */
  hint?: string;
  /** Offered as a list to choose from instead of a box to type in. */
  options?: { value: string; label: string }[];
  /** Translation key or text for the list's empty first line, so nothing is chosen until someone chooses. */
  placeholder?: string;
}

export interface PromptDialogData {
  /** Translation key or text. */
  title: string;
  fields: PromptField[];
  /** Translation key for the confirm button; "OK" when absent. */
  confirm?: string;
}

/**
 * Asks for one or a few short values — a name, an id, a list.
 *
 * It replaces `window.prompt`, which took the page's focus away, could not be
 * styled or translated beyond its question, and asked for related values one
 * box at a time (a group's id, then separately its name). Closes with the
 * trimmed values by key, or with nothing when cancelled.
 */
@Component({
  selector: 'app-prompt-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <h2 mat-dialog-title>{{ data.title | translate }}</h2>
    <mat-dialog-content class="aca-prompt">
      <mat-form-field appearance="outline" *ngFor="let f of data.fields; let first = first">
        <mat-label>{{ f.label | translate }}</mat-label>
        <select
          *ngIf="f.options"
          matNativeControl
          [(ngModel)]="values[f.key]"
          [required]="!!f.required"
          [attr.cdkFocusInitial]="first ? '' : null"
          [attr.data-automation-id]="'dms-prompt-' + f.key"
        >
          <option *ngIf="f.placeholder" value="" disabled>{{ f.placeholder | translate }}</option>
          <option *ngFor="let o of f.options" [value]="o.value">{{ o.label }}</option>
        </select>
        <input
          *ngIf="!f.options"
          matInput
          [(ngModel)]="values[f.key]"
          [required]="!!f.required"
          [attr.cdkFocusInitial]="first ? '' : null"
          [attr.data-automation-id]="'dms-prompt-' + f.key"
          (keydown.enter)="confirm()"
        />
        <mat-hint *ngIf="f.hint">{{ f.hint | translate }}</mat-hint>
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="close()" data-automation-id="dms-prompt-cancel">{{ 'DMS.WF_DIALOG.CANCEL' | translate }}</button>
      <button mat-flat-button color="primary" type="button" (click)="confirm()" [disabled]="!isValid()" data-automation-id="dms-prompt-confirm">
        {{ data.confirm || 'DMS.WF_DIALOG.CONFIRM' | translate }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .aca-prompt {
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: min(380px, 80vw);
      }
    `
  ]
})
export class PromptDialogComponent {
  readonly data = inject<PromptDialogData>(MAT_DIALOG_DATA);
  private readonly ref = inject(MatDialogRef<PromptDialogComponent>);

  readonly values: Record<string, string> = Object.fromEntries(this.data.fields.map((f) => [f.key, f.value ?? '']));

  isValid(): boolean {
    return this.data.fields.every((f) => !f.required || (this.values[f.key] ?? '').trim().length > 0);
  }

  confirm(): void {
    if (this.isValid()) {
      this.ref.close(Object.fromEntries(Object.entries(this.values).map(([k, v]) => [k, (v ?? '').trim()])));
    }
  }

  close(): void {
    this.ref.close();
  }
}
